import React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useFeature } from '../../context/AuthContext';
import './announcement.css';

// Dismissal is remembered per announcement id for the SESSION only, so the
// banner comes back on the next login. sessionStorage rather than localStorage
// on purpose: a holiday notice people have to plan around is worth a second
// look tomorrow, and a permanent dismissal on day one would silence it for the
// rest of an eleven-day window.
const DISMISSED_KEY = 'rtg.announcementDismissed';

export function readDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY);
  } catch {
    // Private windows and blocked site data throw on access. A banner that
    // cannot remember a dismissal is a far smaller problem than one that
    // crashes the page it sits on, so treat it as "nothing dismissed".
    return null;
  }
}

function rememberDismissed(id) {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, String(id));
  } catch {
    /* ignore — see readDismissed */
  }
}

/**
 * Format a bare 'YYYY-MM-DD' from the API as dd/mm/yyyy.
 *
 * Deliberately NOT lib/formatDate: that runs the value through `new Date`,
 * which parses a date-only string as UTC midnight and then renders it in local
 * time -- so west of Greenwich "2026-08-27" prints as 26/08/2026. A date has no
 * timezone, so it is reordered as text and never becomes an instant.
 */
export function formatDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').slice(0, 10));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** The first line of the body, as a teaser next to the title. */
export function firstLine(body) {
  if (!body) return '';
  const lines = String(body).split('\n').map((l) => l.trim()).filter(Boolean);
  return lines[0] || '';
}

/** Everything after the teaser line, still as separate lines. */
export function restLines(body) {
  if (!body) return [];
  const lines = String(body).split('\n').map((l) => l.trim());
  // Drop leading blanks and the teaser itself, then trim trailing blanks.
  let seen = false;
  const out = [];
  for (const l of lines) {
    if (!seen) {
      if (l) seen = true;
      continue;
    }
    out.push(l);
  }
  while (out.length && !out[out.length - 1]) out.pop();
  return out;
}

/**
 * The sticky announcement strip, under the topbar on every employee page.
 *
 * Renders NOTHING at all when there is no announcement in window, when the
 * flag is off, or when the fetch fails: an empty bar that says "no
 * announcements" is a permanent piece of furniture nobody asked for, and a
 * failed fetch has nothing true to say.
 */
export default function AnnouncementBanner() {
  const enabled = useFeature('announcements');
  const [announcement, setAnnouncement] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const [dismissedId, setDismissedId] = React.useState(() => readDismissed());

  React.useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    axios
      .get(`${API_BASE_URL}/api/announcements/current`, { withCredentials: true })
      .then((res) => {
        if (live) setAnnouncement(res.data?.announcement || null);
      })
      .catch(() => {
        // Silent by design. There is no useful thing to tell someone about a
        // banner that could not load, and a toast here would fire on every
        // page load during an outage.
        if (live) setAnnouncement(null);
      });
    return () => { live = false; };
  }, [enabled]);

  if (!enabled || !announcement) return null;
  if (dismissedId && String(dismissedId) === String(announcement.id)) return null;

  const teaser = firstLine(announcement.body);
  const rest = restLines(announcement.body);
  const hasMore = rest.length > 0;
  const bodyId = `announcement-body-${announcement.id}`;

  const dismiss = () => {
    rememberDismissed(announcement.id);
    setDismissedId(String(announcement.id));
  };

  return (
    // `role="status"` rather than `role="alert"`: an alert interrupts a screen
    // reader mid-sentence, which a holiday notice has not earned.
    <aside className="announcement" role="status" aria-label="Company announcement">
      <div className="announcement-main">
        <span className="announcement-eyebrow">Announcement</span>
        <p className="announcement-title">{announcement.title}</p>
        {teaser && <p className="announcement-teaser">{teaser}</p>}
        {announcement.ends_on && (
          <p className="announcement-dates">
            {announcement.starts_on && announcement.starts_on !== announcement.ends_on
              ? `${formatDay(announcement.starts_on)} – ${formatDay(announcement.ends_on)}`
              : formatDay(announcement.ends_on)}
          </p>
        )}
      </div>

      <div className="announcement-actions">
        {hasMore && (
          <button
            type="button"
            className="announcement-more"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-controls={bodyId}
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
        <button
          type="button"
          className="announcement-dismiss"
          onClick={dismiss}
          aria-label="Dismiss announcement"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      {hasMore && expanded && (
        <div className="announcement-body" id={bodyId}>
          {/* Odoo stores the body as plain text with newlines and emoji. Rendered
              as text nodes, one paragraph per line -- never innerHTML, since
              this string is authored in Odoo and reaches every employee. */}
          {rest.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            line ? <p key={i}>{line}</p> : <br key={i} />
          ))}
        </div>
      )}
    </aside>
  );
}
